/**
 * The recipe, in full, editable.
 *
 * Every field the parser filled shows WHERE it came from, and editing one flips
 * it to hers. That flip is the point: after she has been through a recipe once,
 * the app should stop claiming the caption said things she actually decided.
 */
import { useCallback, useEffect, useState } from 'react'
import config from '../../../studio42.config.json'
import { nudgeState } from '../../../shared/nudge'
import { CheckIn, whyWaiting } from './Waiting'
import Icon from '../Icon'
import DeleteRecipeButton from './DeleteRecipeButton'
import { AddCategory, Attention, CategoryChips, ProvenanceTag, SourceLink } from './Bits'
import {
  STATUS_LABEL, addCategoryToRecipe, formatDuration, getRecipe, history,
  recordPastMake, removeCategoryFromRecipe, resolveFlag, saveEquipment,
  saveIngredients, saveSteps, updateRecipe,
  type Ingredient, type Recipe, type Run, type Status, type Step,
} from '../../lib/savenmake'

export default function RecipeEditor({ recipeId, onBack, onCook, onChanged }: {
  recipeId: string
  onBack: () => void
  onCook: (id: string) => void
  onChanged: () => void
}) {
  const [recipe, setRecipe] = useState<Recipe | null>(null)
  const [runs, setRuns] = useState<Run[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    const [r, h] = await Promise.all([getRecipe(recipeId), history(recipeId)])
    setRecipe(r)
    setRuns(h)
  }, [recipeId])

  useEffect(() => { void reload().catch((e) => setError(String(e))) }, [reload])

  if (error) return <div className="card"><p className="error">{error}</p><button onClick={onBack}>Back</button></div>
  if (!recipe) return <p className="muted">Loading…</p>

  const apply = (next: Recipe) => { setRecipe(next); onChanged() }
  const nudge = nudgeState(recipe, config.timezone, Date.now())

  const field = async (patch: Record<string, unknown>) => {
    setSaving(true)
    try { apply(await updateRecipe(recipe.id, patch)) } finally { setSaving(false) }
  }

  const commitIngredients = async (rows: Ingredient[]) => {
    setSaving(true)
    try { apply(await saveIngredients(recipe.id, rows)) } finally { setSaving(false) }
  }
  const commitSteps = async (rows: Step[]) => {
    setSaving(true)
    try { apply(await saveSteps(recipe.id, rows)) } finally { setSaving(false) }
  }

  return (
    <>
      <div className="filter-row" style={{ marginBottom: 16 }}>
        <button type="button" className="ghost" onClick={onBack}><Icon name="back" size={16} /> Library</button>
        <DeleteRecipeButton id={recipe.id} title={recipe.title} disabled={saving} onDeleted={onBack} />
        <span className="muted small" style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : `v${recipe.revision}`}</span>
        <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => onCook(recipe.id)}>
          <Icon name="flame" size={16} /> Begin recipe
        </button>
      </div>

      {nudge && (
        <div className="outcome partial">
          <h3><Icon name={nudge.kind === 'planned' ? 'calendar' : 'clock'} size={18} /> {whyWaiting(nudge)}</h3>
          <CheckIn recipe={recipe} state={nudge} onChanged={() => { void reload(); onChanged() }} />
        </div>
      )}

      <Attention flags={recipe.flags} onResolve={async (f) => apply(await resolveFlag(recipe.id, f.id))} />

      <div className="card">
        <label>
          Title
          <input value={recipe.title} onChange={(e) => setRecipe({ ...recipe, title: e.target.value })} onBlur={(e) => void field({ title: e.target.value })} />
        </label>
        <label>
          Description
          <textarea rows={2} value={recipe.description ?? ''} onChange={(e) => setRecipe({ ...recipe, description: e.target.value })} onBlur={(e) => void field({ description: e.target.value || null })} />
        </label>

        <div className="step-meta">
          <label>
            Status
            <select value={recipe.status} onChange={(e) => void field({ status: e.target.value as Status })}>
              {(Object.keys(STATUS_LABEL) as Status[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </label>
          <label>
            Serves
            <input type="number" inputMode="numeric" value={recipe.servings ?? ''} onChange={(e) => setRecipe({ ...recipe, servings: e.target.value ? Number(e.target.value) : null })} onBlur={(e) => void field({ servings: e.target.value ? Number(e.target.value) : null })} />
          </label>
          <label>
            Total minutes
            <input type="number" inputMode="numeric" value={recipe.totalMinutes ?? ''} onChange={(e) => setRecipe({ ...recipe, totalMinutes: e.target.value ? Number(e.target.value) : null })} onBlur={(e) => void field({ totalMinutes: e.target.value ? Number(e.target.value) : null })} />
          </label>
          <label>
            Made before
            <input
              type="number" inputMode="numeric" placeholder="roughly"
              value={recipe.priorMakes ?? ''}
              onChange={(e) => setRecipe({ ...recipe, priorMakes: e.target.value ? Number(e.target.value) : null })}
              onBlur={(e) => void field({ priorMakes: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
        </div>
        {/* Why this is not a list of dated runs, said where she enters it. */}
        <p className="muted small">
          &ldquo;Made before&rdquo; is a rough count from before you had the app. It is kept separate from
          the dated history below, which only records cooks you actually finished here.
        </p>

        <label style={{ marginTop: 8 }}>
          Where it came from
          <input value={recipe.sourceUrl ?? ''} placeholder="https://…" onChange={(e) => setRecipe({ ...recipe, sourceUrl: e.target.value })} onBlur={(e) => void field({ sourceUrl: e.target.value || null })} />
        </label>
        <SourceLink url={recipe.sourceUrl} platform={recipe.sourcePlatform} creator={recipe.sourceCreator} />
      </div>

      <div className="card">
        <h2>Categories</h2>
        <CategoryChips
          categories={recipe.categories}
          onKeep={async (c) => apply(await addCategoryToRecipe(recipe.id, { categoryId: c.id }))}
          onRemove={async (c) => apply(await removeCategoryFromRecipe(recipe.id, c.id))}
        />
        {recipe.categories.some((c) => c.source === 'suggested') && (
          <p className="muted small">Dashed ones are suggestions. Tick to keep, cross to drop.</p>
        )}
        <AddCategory onAdd={async (kind, name) => apply(await addCategoryToRecipe(recipe.id, { kind, name }))} />
      </div>

      <div className="editor-grid two">
        <Ingredients recipe={recipe} onSave={commitIngredients} />
        <Equipment recipe={recipe} onSave={async (names) => apply(await saveEquipment(recipe.id, names))} />
      </div>

      <Steps recipe={recipe} onSave={commitSteps} />

      <History recipe={recipe} runs={runs} onRecorded={reload} />
    </>
  )
}

/* ── ingredients ────────────────────────────────────────────────────────── */

function Ingredients({ recipe, onSave }: { recipe: Recipe; onSave: (rows: Ingredient[]) => Promise<void> }) {
  const [rows, setRows] = useState(recipe.ingredients)
  useEffect(() => setRows(recipe.ingredients), [recipe.ingredients])

  /* Editing a value makes it HERS. The provenance is not decorative — it is what
     stops the app claiming the post said something she decided. */
  const edit = (i: number, patch: Partial<Ingredient>) =>
    setRows((held) => held.map((r, n) => (n === i ? { ...r, ...patch, provenance: 'user', confidence: 'certain' } : r)))

  return (
    <div className="card">
      <h2>Ingredients</h2>
      {rows.map((r, i) => (
        <div className="line-item" key={r.id || i}>
          <input
            type="text" inputMode="decimal" placeholder="qty" aria-label="Quantity"
            value={r.quantity ?? ''} onChange={(e) => edit(i, { quantity: e.target.value ? Number(e.target.value) : null })}
          />
          <input type="text" placeholder="unit" aria-label="Unit" value={r.unit ?? ''} onChange={(e) => edit(i, { unit: e.target.value || null })} />
          <div className="wide">
            <input type="text" placeholder="ingredient" aria-label="Ingredient" value={r.item} onChange={(e) => edit(i, { item: e.target.value })} />
            <ProvenanceTag provenance={r.provenance} confidence={r.confidence} />
          </div>
          <button type="button" className="drop" aria-label={`Remove ${r.item}`} onClick={() => setRows(rows.filter((_, n) => n !== i))}>
            <Icon name="close" size={15} />
          </button>
        </div>
      ))}
      <div className="filter-row" style={{ marginTop: 12 }}>
        <button type="button" onClick={() => setRows([...rows, { id: '', position: rows.length, quantity: null, unit: null, item: '', note: null, raw: null, provenance: 'user', confidence: 'certain' }])}>
          <Icon name="plus" size={16} /> Add
        </button>
        <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => void onSave(rows)}>Save ingredients</button>
      </div>
    </div>
  )
}

function Equipment({ recipe, onSave }: { recipe: Recipe; onSave: (names: { name: string }[]) => Promise<void> }) {
  const [rows, setRows] = useState(recipe.equipment.map((e) => e.name))
  useEffect(() => setRows(recipe.equipment.map((e) => e.name)), [recipe.equipment])
  return (
    <div className="card">
      <h2>Equipment</h2>
      {rows.map((name, i) => (
        <div className="filter-row" key={i} style={{ marginBottom: 6 }}>
          <input className="grow" value={name} aria-label="Equipment" onChange={(e) => setRows(rows.map((r, n) => (n === i ? e.target.value : r)))} style={{ marginTop: 0 }} />
          <button type="button" className="drop" aria-label="Remove" onClick={() => setRows(rows.filter((_, n) => n !== i))}><Icon name="close" size={15} /></button>
        </div>
      ))}
      <div className="filter-row" style={{ marginTop: 12 }}>
        <button type="button" onClick={() => setRows([...rows, ''])}><Icon name="plus" size={16} /> Add</button>
        <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => void onSave(rows.filter(Boolean).map((name) => ({ name })))}>Save equipment</button>
      </div>
    </div>
  )
}

/* ── steps ──────────────────────────────────────────────────────────────── */

const ACTIONS: { key: Step['actionKind']; label: string }[] = [
  { key: 'prep', label: 'Prep' },
  { key: 'oven', label: 'Oven' },
  { key: 'stovetop', label: 'Stovetop' },
  { key: 'rest', label: 'Rest / rise' },
  { key: 'chill', label: 'Chill' },
]

function Steps({ recipe, onSave }: { recipe: Recipe; onSave: (rows: Step[]) => Promise<void> }) {
  const [rows, setRows] = useState(recipe.steps)
  useEffect(() => setRows(recipe.steps), [recipe.steps])

  const edit = (i: number, patch: Partial<Step>) =>
    setRows((held) => held.map((r, n) => (n === i ? { ...r, ...patch, provenance: 'user', confidence: 'certain' } : r)))

  const swap = (i: number, j: number) => {
    if (j < 0 || j >= rows.length) return
    const next = [...rows]
    ;[next[i], next[j]] = [next[j]!, next[i]!]
    setRows(next)
  }

  return (
    <div className="card">
      <h2>Method</h2>
      <p className="muted small">
        The method matters as much as the ingredients — it is what the cooking screen walks through.
        An <b>Oven</b> step asks you to confirm before its timer starts.
      </p>

      {rows.map((s, i) => (
        <div className="step-item" key={s.id || i}>
          <div className="filter-row">
            <b>Step {i + 1}</b>
            <ProvenanceTag provenance={s.provenance} confidence={s.confidence} />
            <span style={{ marginLeft: 'auto' }} />
            <button type="button" onClick={() => swap(i, i - 1)} disabled={i === 0} aria-label="Move up">↑</button>
            <button type="button" onClick={() => swap(i, i + 1)} disabled={i === rows.length - 1} aria-label="Move down">↓</button>
            <button type="button" aria-label="Remove step" onClick={() => setRows(rows.filter((_, n) => n !== i))}><Icon name="close" size={15} /></button>
          </div>
          <textarea value={s.text} onChange={(e) => edit(i, { text: e.target.value })} aria-label={`Step ${i + 1}`} />
          <div className="step-meta">
            <label>
              Kind
              <select value={s.actionKind ?? 'prep'} onChange={(e) => edit(i, { actionKind: e.target.value as Step['actionKind'] })}>
                {ACTIONS.map((a) => <option key={a.key} value={a.key ?? ''}>{a.label}</option>)}
              </select>
            </label>
            <label>
              Temperature
              <input type="number" inputMode="numeric" placeholder="none given" value={s.temperature ?? ''} onChange={(e) => edit(i, { temperature: e.target.value ? Number(e.target.value) : null })} />
            </label>
            <label>
              Unit
              <select value={s.temperatureUnit ?? ''} onChange={(e) => edit(i, { temperatureUnit: (e.target.value || null) as 'F' | 'C' | null })}>
                <option value="">—</option>
                <option value="F">°F</option>
                <option value="C">°C</option>
              </select>
            </label>
            <label>
              Minutes
              <input
                type="number" inputMode="numeric" placeholder="none given"
                value={s.durationSeconds === null ? '' : Math.round(s.durationSeconds / 60)}
                onChange={(e) => edit(i, { durationSeconds: e.target.value ? Number(e.target.value) * 60 : null })}
              />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              Look for — how you know it is done
              <input value={s.cue ?? ''} placeholder="until golden brown" onChange={(e) => edit(i, { cue: e.target.value || null })} />
            </label>
          </div>
          {s.durationSeconds !== null && <p className="muted small" style={{ margin: 0 }}>Timer: {formatDuration(s.durationSeconds)}</p>}
        </div>
      ))}

      <div className="filter-row" style={{ marginTop: 14 }}>
        <button type="button" onClick={() => setRows([...rows, { id: '', position: rows.length, text: '', temperature: null, temperatureUnit: null, durationSeconds: null, actionKind: 'prep', cue: null, ingredientRefs: [], provenance: 'user', confidence: 'certain' }])}>
          <Icon name="plus" size={16} /> Add a step
        </button>
        <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => void onSave(rows)}>Save the method</button>
      </div>
    </div>
  )
}

/* ── this recipe's history ──────────────────────────────────────────────── */

function History({ recipe, runs, onRecorded }: { recipe: Recipe; runs: Run[]; onRecorded: () => void }) {
  const [adding, setAdding] = useState(false)
  const [when, setWhen] = useState('')
  const [notes, setNotes] = useState('')

  return (
    <div className="card">
      <h2>Cooked</h2>
      {!runs.length && <p className="muted">Not cooked through the app yet.</p>}
      <ul className="list">
        {runs.map((run) => (
          <li key={run.id}>
            <div>
              <b>{run.cookedOn}</b>
              {run.source === 'manual' && <span className="muted small"> · recorded by hand</span>}
              {run.servingsActual && <span className="muted small"> · made {run.servingsActual}</span>}
              {run.notes && <div className="small">{run.notes}</div>}
              {run.adjustments && <div className="small"><b>Changed:</b> {run.adjustments}</div>}
              {!!run.tags.length && <div className="chips" style={{ marginTop: 6 }}>{run.tags.map((t) => <span key={t} className="chip">{t}</span>)}</div>}
              {/* The snapshot rule, made visible. */}
              {run.recipeChangedSince && <div className="prov prov-suggested">the recipe has changed since this cook</div>}
            </div>
          </li>
        ))}
      </ul>

      {adding ? (
        <form
          className="filter-row"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!when) return
            await recordPastMake({ recipeId: recipe.id, cookedOn: when, notes: notes.trim() || null })
            setAdding(false); setWhen(''); setNotes('')
            onRecorded()
          }}
        >
          <input type="date" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Date cooked" style={{ marginTop: 0, maxWidth: 180 }} />
          <input className="grow" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes — optional" aria-label="Notes" style={{ marginTop: 0 }} />
          <button type="submit" className="primary" style={{ width: 'auto' }} disabled={!when}>Record it</button>
          <button type="button" className="ghost" onClick={() => setAdding(false)}>Cancel</button>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)}><Icon name="plus" size={16} /> Record a past cook</button>
      )}
    </div>
  )
}
